import classNames from "classnames";
import * as React from "react";

import type { ActivityBucket } from "./activity";
import { formatWindow } from "./formatRange";
import styles from "./TimelineActivity.module.css";
import { useTimelineContext } from "./TimelineContext";
import { percent } from "./trackStyle";
import { useBrushGestures } from "./useBrushGestures";
import { fractionOf, MIN_SPAN, spanOf, type TimeWindow } from "./window";

interface TimelineActivityProps {
  /** The whole extent, bucketed; each bucket with any activity draws a line. */
  buckets: readonly ActivityBucket[];
}

/**
 * A view of all the timeline's activity
 */
export function TimelineActivity({ buckets }: TimelineActivityProps) {
  const { timeWindow, extent, setTimeWindow, format } = useTimelineContext();
  // A history that has not lasted `MIN_SPAN` yet (its only session started
  // at "now") is drawn as its last `MIN_SPAN`, so nothing divides by zero.
  const domain = React.useMemo(
    () =>
      spanOf(extent) >= MIN_SPAN
        ? extent
        : { start: extent.end - MIN_SPAN, end: extent.end },
    [extent],
  );
  const { dragging, draggedWindow, selection, handlers } = useBrushGestures({
    domain,
    timeWindow,
    setTimeWindow,
  });

  // The share of the strip a window covers, clipped to it.
  const share = (window: TimeWindow) => {
    const left = Math.max(fractionOf(domain, window.start), 0);
    const right = Math.min(fractionOf(domain, window.end), 1);
    return { left, width: Math.max(right - left, 0) };
  };
  const selected = selection && share(selection);
  const mark = share(draggedWindow ?? timeWindow);

  const history = formatWindow(extent, format);
  const shown = formatWindow(timeWindow, format);
  const label = `Activity from ${history.text}. Showing ${shown.text}.`;

  const edgeDate = (ms: number) =>
    format.format(new Date(ms), { time: "none" });

  return (
    <div className={styles.activity}>
      <span className={styles.edgeDate} aria-hidden="true">
        {edgeDate(extent.start)}
      </span>
      <div
        className={classNames(styles.plot, dragging && styles[dragging])}
        role="img"
        aria-label={label}
        {...handlers}
      >
        <ActivityLines buckets={buckets} domain={domain} />
        {selected && (
          <div
            className={styles.selection}
            style={{
              left: percent(selected.left),
              width: percent(selected.width),
            }}
          />
        )}
        <div
          className={styles.mark}
          style={
            {
              "--mark-centre": percent(mark.left + mark.width / 2),
              "--mark-width": percent(mark.width),
            } as React.CSSProperties
          }
          data-brush="move"
        >
          <span className={styles.handle} data-brush="start" />
          <span
            className={classNames(styles.handle, styles.handleEnd)}
            data-brush="end"
          />
        </div>
      </div>
      <span className={styles.edgeDate} aria-hidden="true">
        {edgeDate(extent.end)}
      </span>
    </div>
  );
}

/**
 * One line per bucket with any activity, at the bucket's centre. Memoised:
 * the buckets change with the data, not with every pan, and there are
 * hundreds of them.
 */
const ActivityLines = React.memo(function ActivityLines({
  buckets,
  domain,
}: {
  buckets: readonly ActivityBucket[];
  domain: TimeWindow;
}) {
  return buckets.map(
    (bucket) =>
      bucket.activeMs > 0 && (
        <div
          key={bucket.startTime}
          className={styles.line}
          data-activity-line
          style={{
            left: percent(
              fractionOf(domain, (bucket.startTime + bucket.endTime) / 2),
            ),
          }}
        />
      ),
  );
});
